import React, {h, Component} from 'preact';
import {PropTypes} from 'preact-compat';
import ModalFormPartial from './MobileModalFormPartial';
import ImageInput from '../AgentOnboard/ImageInput';
import DateTimePickerInput from '../DateTimePickerInput';

import './transactions-page.scss';
import {pluralize} from '../../../v2/src/main/resources/jsx/string-util';

const DEFAULT_TX_PER_PAGE = 12;

export default class Transactions extends Component {
    static get propTypes() {
        return {
            realtorId: PropTypes.number,
            noRedirect: PropTypes.bool,
            isAdmin: PropTypes.bool
        };
    }
    state = {
        transactions: {},
        updatedTransactions: {},
        deletedTransactions: {},
        featuredTransactionsCount: 0,
        isMobile: false,
        modalVisible: false,
        submitting: false,
        errors: {},
        total: 0,
        totalPages: '',
        currentPage: 1,
        isLoading: false,
        uploadingImage: false,
        realtorId: this.props.realtorId,
        isValidDate: true
    };

    autoComplete = {};
    selectedItemMobileId = null;
    minTransactions = 1;
    mql;
    transactionsPerPage = DEFAULT_TX_PER_PAGE;

    componentWillMount() {
        this.mql = window.matchMedia('(min-width: 768px)');
        this.setState({isMobile: !this.mql.matches});
        this.mql.addListener(this.handleMediaQuery);
        this.transactionsPerPage = this.mql?.matches ? DEFAULT_TX_PER_PAGE : 6;
    }

    componentDidMount() {
        this.loadMoreTransactions(0, false);
    }

    componentWillUnmount() {
        this.mql.removeListener(this.isMobile);
        Object.keys(this.autoComplete).forEach(ac => google.maps.event.clearInstanceListeners(this.autoComplete[ac]));
    }

    handleMediaQuery = ({matches}) => {
        this.setState({isMobile: !matches});
        this.transactionsPerPage = matches ? DEFAULT_TX_PER_PAGE : 6;
    };

    initGoogleAutoComplete = (ref, txId, tx) => {
        const autoCompleteId = `address_auto_complete_${txId}`;
        if (ref === null || this.autoComplete[autoCompleteId]) {
            return;
        }

        this.autoComplete[autoCompleteId] = new google.maps.places.Autocomplete(ref, {
            types: ['address'],
            componentRestrictions: {country: 'us'}
        });

        this.autoComplete[autoCompleteId].addListener('place_changed', () => {
            const {formatted_address, address_components, geometry} = this.autoComplete[autoCompleteId].getPlace();
            const address = (formatted_address || '').replace(/\s*[0-9]+,?\s+(USA|United States)$/, '');

            this.updateTransactions(tx || txId, {
                address,
                zip: getPlaceAddressComponentByType(address_components, 'postal_code')
            });

            if (geometry && geometry.location) {
                this.updateTransactions(tx || txId, {
                    latitude: geometry.location.lat(),
                    longitude: geometry.location.lng()
                });
            }
        });
    };

    handleAddressInput = ({target: {value: address}}, tx) => {
        this.updateTransactions(tx, {address});
    };

    updateTransactions = (tx, data) => {
        if (typeof tx === 'string') {
            this.setState(currentState => {
                const updatedTx = {...currentState.transactions[tx], ...data};
                return {
                    featuredTransactionsCount:
                        data.featured == undefined
                            ? currentState.featuredTransactionsCount
                            : data.featured
                            ? currentState.featuredTransactionsCount + 1
                            : currentState.featuredTransactionsCount - 1,
                    transactions: {
                        ...currentState.transactions,
                        [tx]: updatedTx
                    },
                    updatedTransactions: {
                        ...currentState.updatedTransactions,
                        [tx]: {...updatedTx, action: !!updatedTx.txId ? 'update' : 'add'}
                    }
                };
            });
            return;
        } else if (Object.keys(this.state.transactions).length <= 500) {
            this.setState(currentState => ({
                transactions: {
                    ...currentState.transactions,
                    [`transaction_${Object.keys(currentState.transactions).length + 1}`]: {
                        ...data
                    }
                },
                updatedTransactions: {
                    ...currentState.updatedTransactions,
                    [`transaction_${Object.keys(currentState.transactions).length + 1}`]: {
                        ...data,
                        action: !!data.txId ? 'update' : 'add'
                    }
                }
            }));
            return;
        } else {
            this.setState({errors: {general: 'Only allow up to 500 transactions.'}});
            return;
        }
    };

    validateTransaction = tx => {
        return (
            !!tx.address && !!tx.zip && !!tx.soldPrice && !!tx.beds && !!tx.baths && !!tx.finishedSqFt && !!tx.soldDate
        );
    };

    pruneTransactions = tx => {
        const currentTransactions = this.state.transactions;
        const updatedTransactions = this.state.updatedTransactions;
        if (tx) {
            if (currentTransactions[tx]?.txId) {
                this.setState(currentState => ({
                    deletedTransactions: {
                        ...currentState.deletedTransactions,
                        [tx]: {...currentTransactions[tx], action: 'delete'}
                    }
                }));
            }
            delete updatedTransactions[tx];
            delete currentTransactions[tx];
        } else {
            Object.keys(currentTransactions).forEach(tx => {
                if (!this.validateTransaction([currentTransactions[tx]])) {
                    delete currentTransactions[tx];
                    delete updatedTransactions[tx];
                }
            });
        }
        if (this.autoComplete[`address_auto_complete_${tx}`]) {
            google.maps.event.clearInstanceListeners(this.autoComplete[`address_auto_complete_${tx}`]);
            delete this.autoComplete[`address_auto_complete_${tx}`];
        }
        this.setState({transactions: {...currentTransactions}, updatedTransactions});
    };

    denormalizePrice = value => {
        if (!value) {
            return '';
        }
        const normalizedValue = typeof value === 'string' ? this.normalizePrice(value) : value;
        return '' + normalizedValue.toFixed(0).replace(/\d(?=(\d{3})+$)/g, '$&,');
    };

    normalizePrice = value => {
        if (typeof value === 'number') {
            return value;
        }
        if (!value || typeof value !== 'string') {
            return 0;
        }
        return +value.replace(/\D+/g, '');
    };

    collectTransactionData = transaction => ({
        txId: transaction.txId,
        address: transaction.prop?.fullAddressRaw,
        zip: transaction.prop?.zip,
        baths: transaction.prop?.baths,
        beds: transaction.prop?.beds,
        finishedSqFt: transaction.prop?.finishedSqFt,
        listPrice: transaction.price,
        soldPrice: transaction.soldPrice,
        buy: transaction.buy,
        soldDate: new Date(transaction.txDate),
        type: transaction.type,
        featured: transaction.featured,
        photoUrl: transaction.prop?.photoUrl
    });

    submitTransactions = e => {

        e.preventDefault();
        this.setState({errors: {}});

        const transactionsToSave = {...this.state.deletedTransactions, ...this.state.updatedTransactions};
        let successfulSubmissions = 0;

        const validTransactions = Object.keys(transactionsToSave).filter(tx => {
            if (transactionsToSave[tx].action !== 'delete') {
                if (
                    !transactionsToSave[tx].address ||
                    transactionsToSave[tx].address.length <= 7 ||
                    !formExt.addressHasNumber(transactionsToSave[tx].address)
                ) {
                    this.setState(currentState => ({
                        errors: {
                            ...currentState.errors,
                            [tx]: 'Please select a valid address that starts with a number.'
                        }
                    }));
                    return false;
                }
                else if (
                    !transactionsToSave[tx].address ||
                    transactionsToSave[tx].address.length <= 7 ||
                    !formExt.isValidAddress(transactionsToSave[tx].address, true)
                ) {
                    this.setState(currentState => ({
                        errors: {
                            ...currentState.errors,
                            [tx]: 'Please enter an address that follows the correct format, including commas (123 Main Street, San Diego, CA)'
                        }
                    }));
                    return false;
                }

                this.setState({errors: {}});
                const isValidDate = this.handleDateValidation(transactionsToSave[tx].soldDate);
                if (!isValidDate) {
                    this.setState(currentState => ({
                        isValidDate:false,
                        errors: {
                            ...currentState.errors,
                            [tx]: 'Please enter a valid date .'
                        }
                    }));
                    return false;
                }

                const hasImage = !!transactionsToSave[tx].photoUrl || !!transactionsToSave[tx].propertyImage?.src;
                if (('' + transactionsToSave[tx].soldPrice).replace(/\D+/g, '') < 10000) {
                    this.setState(currentState => ({
                        errors: {
                            ...currentState.errors,
                            [tx]: `The sales price should be above $10,000. Please make sure you provide correct information`
                        }
                    }));
                    return false;
                }

                if (!transactionsToSave[tx].zip || transactionsToSave[tx].zip.length < 5) {
                    this.setState(currentState => ({
                        errors: {
                            ...currentState.errors,
                            [tx]: "Zip code can't be less than 5 digits"
                        }
                    }));
                    return false;
                }

                if (!hasImage && transactionsToSave[tx].featured) {
                    this.setState(currentState => ({
                        errors: {
                            ...currentState.errors,
                            [tx]: 'One or more featured transactions is missing an image.'
                        }
                    }));
                    return false;
                }

                if (transactionsToSave[tx].finishedSqFt > 0 && transactionsToSave[tx].finishedSqFt < 120) {
                    this.setState(currentState => ({
                        errors: {
                            ...currentState.errors,
                            [tx]: 'Square footage should be greater than 120'
                        }
                    }));
                    return false;
                }
            }

            return true;
        });

        if (!!Object.keys(this.state.errors).length) {
            return;
        }

        this.setState({submitting: true});

        const submissionQueue = validTransactions.map(tx => {
            const {
                txId,
                listPrice,
                soldPrice,
                address,
                zip,
                beds,
                baths,
                finishedSqFt,
                soldDate,
                latitude,
                longitude,
                buy,
                propertyImage = {},
                action = 'add',
                featured
            } = transactionsToSave[tx];

            const formDataJSON = {
                'realtorId': this.state.realtorId,
                'listPrice': this.normalizePrice(listPrice || ''),
                'soldPrice': this.normalizePrice(soldPrice || ''),
                'tempFileName': propertyImage.file || '',
                featured,
                'txDate': moment(soldDate).format('YYYY-MM-DD'),
                'prop.address': address,
                'prop.zip': zip,
                'prop.beds': beds,
                'prop.baths': baths,
                'prop.finishedSqFt': finishedSqFt,
                'prop.mapLatitude': latitude,
                'prop.mapLongitude': longitude,
                buy
            };

            if (['update', 'delete'].includes(action)) {
                formDataJSON.txId = txId;
                delete formDataJSON['prop.mapLatitude'];
                delete formDataJSON['prop.mapLongitude'];
            }

            const formData = new FormData();
            for (const key of Object.keys(formDataJSON)) {
                const value = formDataJSON[key];
                if (value != null) {
                    formData.append(key, value);
                }
            }

            const endpointMapping = {
                add: 'addTx-json',
                update: 'editTx-json',
                delete: 'deleteTx-json'
            };

            return $.ajax(endpointMapping[action], {
                method: 'POST',
                data: formData,
                processData: false,
                contentType: false,
                success: data => {
                    if (!(data?.msg || '').includes('has been updated') && !checkAjax(data)) {
                        this.setState({
                            errors: {
                                [tx]: `${data.msg}`
                            }
                        });
                        return $.Deferred()
                            .reject()
                            .promise();
                    } else {
                        successfulSubmissions += 1;
                        this.minTransactions = this.minTransactions - successfulSubmissions;
                        if (action == 'add' || action == 'update') {
                            this.setState(state => {
                                const updatedTransactions = state.updatedTransactions;
                                delete state.updatedTransactions[tx];
                                return {updatedTransactions};
                            });
                        }
                    }
                }
            });
        });

        Promise.all(submissionQueue)
            .then(() => {
                if (successfulSubmissions >= validTransactions.length) {
                    this.setState({errors: {}, submitting: false});
                    trackEvent('Flow', 'Transactions', 'AgentSubmitTransactions');
                    let pageNum =
                        Object.keys(this.state.transactions).length === 0 && this.state.currentPage > 1
                            ? this.state.currentPage - 1
                            : this.state.currentPage;

                    if (this.state.transactions) {
                        if (!this.props.noRedirect) {
                            this.loadMoreTransactions(pageNum, false);
                        }
                    }
                } else {
                    this.setState({submitting: false});
                }
            })
            .finally(() => this.setState({submitting: false}));
    };

    loadMoreTransactions = (pageNum, addNewTransaction) => {
        const pageStart = pageNum > 1 ? this.transactionsPerPage * (pageNum - 1) : 0;
        this.setState({isLoading: true});

        $.get(`listTx-json?start=${pageStart}&pageSize=${this.transactionsPerPage}&realtorId=${this.props.realtorId}`)
            .then(({msg, transactions, total}) => {
                if (!msg) {
                    const transactionsFilled = transactions.reduce((acc, current, index) => {
                        current.soldPrice = current.soldPrice || current.listPrice;
                        return {
                            ...acc,
                            [`transaction_${index}`]: this.collectTransactionData(current)
                        };
                    }, {});

                    this.setState({
                        deletedTransactions: {},
                        transactions: transactionsFilled,
                        featuredTransactionsCount: transactions.filter(tx => tx.featured).length,
                        currentPage: pageNum > 0 ? pageNum : 1,
                        total,
                        totalPages: Math.ceil(total / this.transactionsPerPage)
                    });

                    if (addNewTransaction || (pageNum <= 1 && Object.keys(transactionsFilled).length == 0)) {
                        this.updateTransactions(null, {});
                        setTimeout(() => {
                            $('.add-more').scrollIntoView();
                        }, 200);
                    }
                }
            })
            .always(() => this.setState({isLoading: false}));
    };

    handleDateValidation = (soldDate) => {
        const currentSoldDate = new Date(soldDate);
        const dateFormatPattern = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(19|20)\d{2}$/;

        const dateString = currentSoldDate.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
        const minDate = new Date('01/01/1970');
        const maxDate = new Date(); 
        
        if (!dateFormatPattern.test(dateString) || (currentSoldDate < minDate || currentSoldDate > maxDate)) {
            return false;
         }
         return true;
    };

    handleChangeDate =({target: {value: soldDate}}, tx) => {
        this.setState({errors: {}});
        const isValidDate = this.handleDateValidation(soldDate);

        if (!isValidDate) {
            this.setState(currentState => ({
                isValidDate:false,
                errors: {
                    ...currentState.errors,
                    [tx]: 'Please enter a valid date .'
                }
            }));
            return false;
        } else{
            this.setState(currentState => ({isValidDate:true,
            errors:{}}));
            this.updateTransactions(tx, {soldDate});
        }
    };

    renderTransactionRow = tx => {
        const item = this.state.transactions[tx];
        const error = this.state.errors[tx];
        const hasImage = !!item.propertyImage?.src || !!item.photoUrl;
        const canBeDeleted = true;

        return (
            <div id={tx} key={tx} className={'tx-row' + (error ? ' error' : '')}>
                <div className="tx-cell lg">
                    <input
                        ref={el => this.initGoogleAutoComplete(el, item.txId || tx, tx)}
                        value={item.address || ''}
                        name={`address_${tx}`}
                        type="text"
                        className="transaction-input"
                        placeholder="Full Property Address"
                        onChange={e => this.handleAddressInput(e, tx)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                            }
                        }}
                    />
                </div>
                <div className="tx-cell md date">
                    <DateTimePickerInput
                        options={{
                            date: item.soldDate || null,
                            maxDate: new Date()
                        }}
                        name={`soldDate_${tx}`}
                        className="transaction-input"
                        autoComplete="off"
                        onChange={e => this.handleChangeDate(e,tx)}
                    />
                </div>
                {/*<div className="tx-cell md listprice">
                    <input
                        value={item.listPrice ? this.denormalizeListPrice(item.listPrice) : ''}
                        name={`listPrice_${tx}`}
                        className="transaction-input"
                        placeholder="0"
                        onKeyUp={({target: {value}}) => {
                            const denormalizedValue = this.denormalizeListPrice(value);
                            this.updateTransactions(tx, {
                                listPrice: denormalizedValue
                            });
                        }}
                    />
                </div>*/}
                <div className="tx-cell md listprice">
                    <input
                        value={this.denormalizePrice(item.soldPrice || item.listPrice)}
                        name={`soldPrice_${tx}`}
                        className="transaction-input"
                        placeholder="0"
                        onKeyUp={({target: {value}}) => {
                            const denormalizedValue = this.denormalizePrice(value);
                            this.updateTransactions(tx, {
                                soldPrice: denormalizedValue
                            });
                        }}
                    />
                </div>
                <div className="tx-cell md">
                    <select
                        defaultValue={typeof item.buy !== 'undefined' ? '' + item.buy : ''}
                        name={`buy_${tx}`}
                        type="number"
                        className="transaction-input"
                        placeholder="000000"
                        onChange={({target: {value}}) => this.updateTransactions(tx, {buy: value === 'true'})}>
                        <option value="">Select</option>
                        <option value="false">Sold</option>
                        <option value="true">Bought</option>
                    </select>
                </div>
                <div className="tx-cell sm">
                    <input
                        value={item.zip || ''}
                        name={`zip_${tx}`}
                        type="text"
                        className="transaction-input"
                        placeholder="000000"
                        onChange={({target: {value}}) => this.updateTransactions(tx, {zip: value.replace(/\D+/g, '')})}
                    />

                    <input type="hidden" name={`latitude_${tx}`} value={item.latitude || ''} />
                    <input type="hidden" name={`longitude_${tx}`} value={item.longitude || ''} />
                </div>
                <div className="tx-cell xs">
                    <input
                        value={item.beds || ''}
                        name={`beds_${tx}`}
                        type="text"
                        className="transaction-input"
                        placeholder="0"
                        onChange={({target: {value}}) => this.updateTransactions(tx, {beds: value.replace(/\D+/g, '')})}
                    />
                </div>
                <div className="tx-cell xs">
                    <input
                        value={item.baths || ''}
                        name={`baths_${tx}`}
                        type="text"
                        className="transaction-input"
                        placeholder="0"
                        onChange={({target: {value}}) =>
                            this.updateTransactions(tx, {baths: value.replace(/[^.0-9]/g, '')})
                        }
                    />
                </div>
                <div className="tx-cell sm">
                    <input
                        value={item.finishedSqFt || ''}
                        name={`finishedSqFt_${tx}`}
                        type="text"
                        className="transaction-input"
                        placeholder="0"
                        onChange={({target: {value}}) =>
                            this.updateTransactions(tx, {finishedSqFt: value.replace(/\D+/g, '')})
                        }
                    />
                </div>
                <div className="tx-cell featured full">
                    <div
                        className={`switch-inpunt-wrapper${!!item.featured ? ' active' : ''}`}
                        onClick={() => {
                            if (this.state.featuredTransactionsCount >= 10 && !item.featured) {
                                return;
                            }
                            this.updateTransactions(tx, {featured: !item.featured});
                        }}>
                        <span className="switch-input-dot"></span>
                    </div>

                    <ImageInput
                        name={`propertyImage_${tx}`}
                        className={`image-upload${hasImage ? ' has-image' : ''}`}
                        style={
                            hasImage
                                ? `background-image: url('${(item.propertyImage || {}).src || item.photoUrl}')`
                                : ''
                        }
                        onUploading={uploadingImage => this.setState({uploadingImage})}
                        onChange={propertyImage => {
                            this.setState({uploadingImage: false});
                            this.updateTransactions(tx, {propertyImage});
                        }}>
                        {!hasImage ? imageIcon : null}
                        {!hasImage && <div className="image-upload-text">UPLOAD</div>}
                    </ImageInput>

                    <div
                        className={`delete-btn${canBeDeleted ? '' : ' disabled'}`}
                        onClick={() => {
                            if (!canBeDeleted) {
                                return;
                            }

                            this.pruneTransactions(tx);
                            delete this.autoComplete[`address_${tx}`];
                        }}>
                        <i className="icon-delete delete-button" />
                    </div>
                </div>
            </div>
        );
    };

    renderMobileTransactionRow = tx => {
        const {
            address,
            soldPrice,
            soldDate,
            propertyImage,
            buy,
            photoUrl
        } = this.state.transactions[tx];
        const hasImage = !!propertyImage?.src || !!photoUrl;

        return (
            <div id={tx} key={tx} className={`transaction${!!!address ? ' empty' : ''}`}>
                <div>
                    <div className="tr-item">
                        <div className="address">{address || '123 Main St, New York, NY'}</div>
                        <div
                            className="mobile-edit-btn"
                            onClick={() => {
                                this.selectedItemMobileId = tx;
                                this.setState({modalVisible: true});
                            }}>
                            {!!address ? 'Edit' : 'Add'}
                        </div>
                    </div>
                </div>
                <div className="property-details">
                    <div
                        className={`tr-item property-image${hasImage ? ' has-image' : ''}`}
                        style={hasImage ? `background-image: url('${propertyImage?.src || photoUrl}')` : ''}>
                        {!hasImage && <div className="image-placeholder">{imageIcon}</div>}
                    </div>
                    <div className="tr-item">
                        <div className="tr-details">
                            {buy ? 'Bought' : 'Sold'} <span className="separation-dot" />{' '}
                            {!!soldDate ? moment(soldDate).format('MM/DD/YYYY') : 'mm/dd/yyyy'}
                        </div>
                        <div className="tr-details">
                            Sales price: {!!soldPrice ? this.denormalizePrice(soldPrice) : '$000,000'}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    renderPaginationButtons = () => {
        const {totalPages, currentPage} = this.state;

        if (!totalPages || totalPages < 2) {
            return;
        } else {
            return [...new Array(totalPages)].map((p, i) => {
                const index = i + 1;
                const halfPoint = Math.ceil(totalPages / 2);
                if (totalPages < 10 || index == 1 || index == totalPages) {
                    return (
                        <div
                            onClick={() => {
                                if (index == currentPage) {
                                    return;
                                }
                                this.loadMoreTransactions(index);
                            }}
                            className={`page-number${index == currentPage ? ' current' : ''}`}>
                            {index}
                        </div>
                    );
                } else if (currentPage <= halfPoint) {
                    if (index == halfPoint + 2) {
                        return <div className="page-number">...</div>;
                    } else if (index > halfPoint + 2) {
                        return;
                    }
                } else if (currentPage > halfPoint) {
                    if (index == halfPoint - 1) {
                        return <div className="page-number">...</div>;
                    } else if (index < halfPoint - 1) {
                        return;
                    }
                }

                return (
                    <div
                        key={'pageBtn' + index}
                        onClick={() => {
                            if (index == currentPage) {
                                return;
                            }
                            this.loadMoreTransactions(index);
                        }}
                        className={`page-number${index == currentPage ? ' current' : ''}`}>
                        {index}
                    </div>
                );
            });
        }
    };

    render() {
        const {
            isMobile,
            transactions,
            modalVisible,
            errors,
            submitting,
            totalPages,
            currentPage,
            isLoading,
            featuredTransactionsCount,
            total,
            isValidDate
        } = this.state;

        const {realtorId, isAdmin} = this.props;

        return (
            <div>
                <div className="header-wrapper">
                    <div className="transactions-header">
                        <a className="header-link" href={!isAdmin ? '/re/agentDashboard' : `/re/agentDetail?realtorId=${realtorId}`}>
                            <i className="icon-arrow-direct-left"/> {!isAdmin ? "Go back to Dashboard" : "Go back to Agent's profile"}
                        </a>
                        <h2 className="title">Transactions</h2>
                        <div className="sub-title">
                            Increase your chances of getting matched with buyers and sellers in your area by adding all
                            your transactions.
                        </div>
                    </div>
                </div>
                <div className="transactions-container">
                    <div className="header-block">
                        <div>
                            Fill out the table below with transactions from the last 12 months. <br/>
                            Finally, choose the featured transactions to be shown on your profile.
                        </div>
                        {/* <button
                            type="button"
                            className="section-upload-csv-button"
                            onClick={() =>
                                showUploadTransactionFiles(realtorId, () => {
                                    this.loadMoreTransactions(currentPage, false);
                                })
                            }>
                            Upload CSV
                        </button> */}
                    </div>
                    <div className={`transactions${this.state.uploadingImage ? ' disabled' : ''}`}>
                        <form ref={el => (this.form = el)}>
                            {isLoading ? (
                                <div className="loading">
                                    <i className="icon-update loading-icon" /> <div>Loading transactions...</div>
                                </div>
                            ) : (
                                <div>
                                    {!isMobile ? (
                                        <div className="transactions-table">
                                            <div className="table-head">
                                                <div className="tx-row">
                                                    <div className="tx-header lg">Property Address</div>
                                                    <div className="tx-header md">Date</div>
                                                    {/*<div className="tx-header md">List Price</div>*/}
                                                    <div className="tx-header md">Price</div>
                                                    <div className="tx-header md">Type</div>
                                                    <div className="tx-header sm">ZIP</div>
                                                    <div className="tx-header xs">Beds</div>
                                                    <div className="tx-header xs">Baths</div>
                                                    <div className="tx-header sm">Sq. Ft.</div>
                                                    <div className="tx-header full">Featured</div>
                                                </div>
                                            </div>
                                            <div className="table-body">
                                                {Object.keys(transactions).map(this.renderTransactionRow)}
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <div className="mobile-transactions-list">
                                                {Object.keys(transactions).map(this.renderMobileTransactionRow)}
                                            </div>
                                            <ModalFormPartial
                                                transactions={transactions}
                                                transactionToEdit={this.selectedItemMobileId}
                                                visible={modalVisible}
                                                denormalizePrice={this.denormalizePrice}
                                                onClose={() => this.setState({modalVisible: false})}
                                                imageIcon={imageIcon}
                                                currentPage={currentPage}
                                                featuredTransactionsCount={featuredTransactionsCount}
                                                onDelete={() => {
                                                    this.pruneTransactions(this.selectedItemMobileId);
                                                    this.setState({modalVisible: false});
                                                }}
                                                onSave={({tx, ...data}) => {
                                                    this.updateTransactions(tx, data);
                                                    this.setState({modalVisible: false});
                                                    this.selectedItemMobileId = null;
                                                }}
                                            />
                                        </div>
                                    )}
                                    {!submitting && (
                                        <div
                                            className={`add-more ${
                                                !(!!totalPages && totalPages > 1) ? ' no-pag' : ''
                                            }`}>
                                            {!!totalPages && totalPages > 1 && (
                                                <div className="pagination-wrapper">
                                                    <span className="pagination-text">Page</span>
                                                    {this.renderPaginationButtons()}
                                                </div>
                                            )}

                                            <div
                                                className="add-more-btn"
                                                onClick={() => {
                                                    trackEvent(
                                                        'Flow',
                                                        'Transactions',
                                                        'AgentAddNewTransaction'
                                                    ).withGA();
                                                    if (isMobile) {
                                                        this.selectedItemMobileId = null;
                                                        return this.setState({
                                                            modalVisible: true
                                                        });
                                                    }

                                                    if (currentPage == totalPages || !totalPages) {
                                                        this.updateTransactions(null, {});
                                                    } else {
                                                        this.loadMoreTransactions(totalPages, true);
                                                    }
                                                }}>
                                                <i className="icon-nav_expand"></i> Add new transaction
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="action-buttons-container">
                                {!!Object.keys(errors).length && (
                                    <div className="tx-error text-right mr-2x">{errors[Object.keys(errors)[0]]}</div>
                                )}
                                {this.minTransactions > Object.keys(transactions).length &&
                                    this.minTransactions > total && (
                                        <div className="tx-error text-right mr-2x">
                                            At least{' '}
                                            {pluralize(
                                                this.minTransactions,
                                                'transaction is required',
                                                'transactions are required'
                                            )}
                                            .
                                        </div>
                                    )}
                                {submitting ? (
                                    <div className="submit-button disabled">
                                        <i className="icon-settings-outline loading-icon saving" /> Saving...
                                    </div>
                                ) : (
                                    <input
                                        onClick={this.submitTransactions}
                                        type="submit"
                                        className={`submit-button${
                                            (this.minTransactions > Object.keys(transactions).length &&
                                            this.minTransactions > total) || !isValidDate
                                                ? ' disabled'
                                                : ''
                                        }`}
                                        value="Save Changes"
                                        disabled={
                                            (this.minTransactions > Object.keys(transactions).length &&
                                                this.minTransactions > total) ||
                                            isLoading || !isValidDate
                                        }
                                    />
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }
}

const imageIcon = (
    <svg fill="none" height="19" viewBox="0 0 22 19" width="22" xmlns="http://www.w3.org/2000/svg">
        <g stroke="#416693" stroke-linecap="round" stroke-linejoin="round" stroke-opacity=".5" stroke-width="1.30769">
            <path d="m16.6923 3.61542v-.85262c.0026-.46497-.1797-.91192-.5067-1.2425-.327-.33059-.7719-.51773-1.2369-.52027h-12.20508c-.46498.00254-.9099.18968-1.23689.52027-.32699.33058-.509254.77753-.5067 1.2425v8.2437c-.002554.465.17971.9119.5067 1.2425s.77191.5177 1.23689.5203" />
            <path
                clip-rule="evenodd"
                d="m8.19226 11.6795c0-.4815.39032-.8718.8718-.8718h2.83334c.4815 0 .8718.3903.8718.8718v2.8334c0 .4814-.3903.8717-.8718.8717h-2.83334c-.48148 0-.8718-.3903-.8718-.8717z"
                fill-rule="evenodd"
            />
            <path d="m14.9486 15.3846h2.2885c.4213 0 .7628-.3415.7628-.7628-.0003-.1216-.0302-.2413-.0872-.3487l-2.7897-5.41646c-.1072-.20817-.3217-.339-.5558-.339s-.4486.13083-.5558.339c-.0601.11507-.136.26153-.2188.42369" />
            <path
                clip-rule="evenodd"
                d="m5.5769 7.10259c0-.48148.39032-.87179.8718-.87179h13.2949c.4814 0 .8718.39031.8718.87179v10.02561c0 .4815-.3903.8718-.8718.8718h-13.2949c-.48148 0-.8718-.3903-.8718-.8718z"
                fill-rule="evenodd"
            />
        </g>
    </svg>
);
